import {useCallback, useEffect, useState} from "react";
import {InputActionMeta} from "react-select";

// Bridges a one-frame loading gap in react-select-async-paginate where
// isLoading is briefly false between input changes and internal cache creation.
export function useStableLoadingState<
  T extends {
    isLoading: boolean;
    options: readonly unknown[];
    onInputChange: (value: string, meta: InputActionMeta) => void;
  },
>(result: T): T {
  const [pendingLoad, setPendingLoad] = useState(false);

  const origOnInputChange = result.onInputChange;
  const onInputChange = useCallback(
    (value: string, meta: InputActionMeta) => {
      if (meta.action === "input-change") {
        setPendingLoad(true);
      }
      return origOnInputChange(value, meta);
    },
    [origOnInputChange]
  );

  useEffect(() => {
    if (!pendingLoad) return;
    if (result.isLoading || result.options.length > 0) {
      setPendingLoad(false);
      return;
    }
    const timer = setTimeout(() => setPendingLoad(false), 50);
    return () => clearTimeout(timer);
  }, [pendingLoad, result.isLoading, result.options.length]);

  return {
    ...result,
    onInputChange,
    isLoading: result.isLoading || pendingLoad,
  } as T;
}
